/**
 * Doorly matricule = `{PARTNER_PREFIX 3}-{SEQUENCE 6}-{LUHN 1}`.
 *
 * Exemple : `WEE-000128-9`.
 *
 *  - **Préfixe partenaire** : 3 caractères [A-Z0-9], choisi à la création
 *    du partenaire (UI), unique sur tout le système. Cf. `Partner.matriculePrefix`.
 *  - **Séquence** : compteur entier croissant par partenaire (`Partner.matriculeCounter`),
 *    figé sur l'utilisateur à la création. Jamais réutilisé.
 *  - **Luhn** : chiffre de contrôle mod-10 calculé sur la séquence pour
 *    détecter les fautes de frappe lors d'un appel support.
 *
 * Le matricule est stable à vie. Si un user change de rôle, partner, ou
 * équipe, son matricule reste figé (norme RH classique). Si un user est
 * supprimé puis recréé chez un autre partner, il obtient un nouveau
 * matricule (un nouveau record user).
 */

/** Regex `^[A-Z0-9]{3}$` pour la validation côté schemas Zod. */
export const MATRICULE_PREFIX_REGEX = /^[A-Z0-9]{3}$/;
export const MATRICULE_FULL_REGEX = /^[A-Z0-9]{3}-\d{6}-\d$/;

/**
 * Calcule le chiffre de contrôle Luhn (mod 10) d'une chaîne numérique.
 * Algorithme standard : on parcourt les chiffres de droite à gauche, on
 * double un chiffre sur deux (en sommant les chiffres si > 9), on somme
 * tout. Le chiffre de contrôle est `(10 - sum % 10) % 10`.
 */
export function luhnCheckDigit(num: string): number {
  let sum = 0;
  for (let i = 0; i < num.length; i++) {
    // On parcourt depuis la fin → indice "depuis la droite"
    const fromRight = num.length - 1 - i;
    let digit = num.charCodeAt(fromRight) - 48; // '0' = 48
    if (digit < 0 || digit > 9) {
      throw new Error(`luhnCheckDigit: non-digit at position ${fromRight} of "${num}"`);
    }
    // On double les positions paires depuis la droite (i = 0, 2, 4, …)
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * Vérifie qu'un matricule complet est valide : format `XXX-000000-Y` ET
 * chiffre de contrôle Luhn cohérent avec la séquence.
 */
export function isValidMatricule(matricule: string): boolean {
  if (!MATRICULE_FULL_REGEX.test(matricule)) return false;
  const seq = matricule.slice(4, 10);
  const expected = luhnCheckDigit(seq);
  const actual = matricule.charCodeAt(11) - 48;
  return actual === expected;
}

/**
 * Construit un matricule à partir d'un préfixe partenaire + une séquence.
 * Pad la séquence à 6 chiffres et appose le Luhn. Lève si la séquence
 * dépasse 999999 (1 M utilisateurs / partenaire — largement assez).
 */
export function buildMatricule(prefix: string, sequence: number): string {
  if (!MATRICULE_PREFIX_REGEX.test(prefix)) {
    throw new Error(`buildMatricule: invalid prefix "${prefix}" (expected ${MATRICULE_PREFIX_REGEX})`);
  }
  if (!Number.isInteger(sequence) || sequence < 1 || sequence > 999_999) {
    throw new Error(`buildMatricule: sequence out of range (got ${sequence}, expected 1..999999)`);
  }
  const seq = sequence.toString().padStart(6, '0');
  return `${prefix}-${seq}-${luhnCheckDigit(seq)}`;
}

/**
 * Identifiant de connexion dérivé du matricule, pour les utilisateurs créés
 * sans adresse email (réseaux de boutiques : les vendeurs n'en ont pas, et
 * les réseaux refusent d'en créer — un vendeur joignable directement se
 * constituerait sa propre clientèle au sein du magasin).
 *
 * `WEE-000128-9` → `wee0001289`. Tirets retirés, minuscules. Les deux
 * transformations sont dictées par le terrain : le vendeur saisit ça sur le
 * clavier tactile d'une tablette partagée entre plusieurs personnes, donc ni
 * Maj ni ponctuation à aller chercher. Clerk impose par ailleurs 4 à 64
 * caractères, ce que le format respecte toujours (10 caractères).
 *
 * La transformation est réversible (préfixe = 3 caractères, séquence = 6
 * chiffres, contrôle = 1) : cf. `usernameToMatricule`, utile au support qui
 * reçoit l'identifiant au téléphone et cherche le matricule.
 */
export function matriculeToUsername(matricule: string): string {
  if (!isValidMatricule(matricule)) {
    throw new Error(`matriculeToUsername: matricule invalide "${matricule}"`);
  }
  return matricule.replace(/-/g, '').toLowerCase();
}

/** Regex de l'identifiant dérivé : `xxx` + 6 chiffres + 1 chiffre de contrôle. */
export const CREDENTIAL_USERNAME_REGEX = /^[a-z0-9]{3}\d{7}$/;

/**
 * Inverse de `matriculeToUsername`. Retourne `null` si la chaîne n'a pas la
 * forme attendue ou si le Luhn ne tombe pas juste — ce qui rattrape la faute
 * de frappe d'un vendeur qui dicte son identifiant au support.
 */
export function usernameToMatricule(username: string): string | null {
  const u = username.trim().toLowerCase();
  if (!CREDENTIAL_USERNAME_REGEX.test(u)) return null;
  const matricule = `${u.slice(0, 3).toUpperCase()}-${u.slice(3, 9)}-${u.slice(9)}`;
  return isValidMatricule(matricule) ? matricule : null;
}

/**
 * Suggère un préfixe à partir d'un nom de partenaire (3 premières lettres
 * alpha, uppercase). Sert d'auto-fill UI à la création — l'admin/RP peut
 * éditer librement, l'unicité est vérifiée backend. Si le nom n'a pas
 * 3 lettres alpha (e.g. "OK" ou "123"), retourne une chaîne plus courte
 * que le RP devra compléter.
 */
export function suggestMatriculePrefix(name: string): string {
  const alpha = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritics (U+0300..U+036F)
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
  return alpha.slice(0, 3);
}
